import React, { useState, useEffect, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import Typography from '../../StyledTypography';
import axiosInstance from '../../../../axiosInstance';
import styles from './ApprovalRejectionReasonItem.module.css';
import { convertUTCToLocal } from '../../../utils/dateUtils';

const ApprovalRejectionReasonItem = ({ requestId, status = 'rejected' }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const location = useLocation();

  const fetchData = useCallback(async () => {
    if (!requestId) return;

    setLoading(true);

    try {
      // Determine if the user is an end user based on the route
      const isEndUser = !location.pathname.includes('/admin') && true;

      if (status === 'rejected') {
        // Fetch rejection reasons
        const apiPath = isEndUser
          ? `/api/rejection-reasons/request/enduser/${requestId}`
          : `/api/rejection-reasons/request/${requestId}`;

        const response = await axiosInstance.get(apiPath);

        if (
          response.data &&
          response.data.success &&
          response.data.data.length > 0
        ) {
          // Get the first (and only) rejection reason
          setData(response.data.data[0]);
        } else {
          setData(null);
        }
      } else if (status === 'approved') {
        // For approved, fetch request data to get approval information
        const apiPath = isEndUser
          ? `/api/requests/enduser/${requestId}`
          : `/api/requests/${requestId}`;

        const response = await axiosInstance.get(apiPath);
        const request = response.data;

        // Find the approved request progress
        const approvedProgress = request.requests_progresses?.find(
          (rp) =>
            !rp.active && rp.StageRelation?.Stage?.ApprovalType?.code === 'app'
        );

        if (approvedProgress) {
          setData({
            id: approvedProgress.id,
            requestId: request.id,
            approvedAt: approvedProgress.updatedAt,
            reviewer: approvedProgress.reviewer,
            stage: {
              id: approvedProgress.StageRelation?.Stage?.id,
              name: approvedProgress.StageRelation?.Stage?.name,
              type: approvedProgress.StageRelation?.Stage?.StageType?.type,
              approvalType: approvedProgress.StageRelation?.Stage?.ApprovalType,
            },
          });
        } else {
          setData(null);
        }
      }
    } catch (err) {
      console.error(`Error fetching ${status} reasons:`, err);
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [requestId, status, location.pathname]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  if (loading) {
    return (
      <div
        className={`${styles.loadingContainer} ${
          status === 'approved' ? styles.approved : ''
        }`}
      >
        <Typography variant="bodyDefault">
          Loading {status} details...
        </Typography>
      </div>
    );
  }

  if (!data) {
    return null;
  }

  // format date
  const formattedDate = convertUTCToLocal(data.rejectedAt || data.approvedAt);

  // Determine the CSS class based on status
  const getStatusClass = () => {
    if (status === 'approved') {
      return styles.approved;
    }
    return styles.rejected;
  };

  const statusClass = getStatusClass();
  const isApproved = status === 'approved';

  const username =
    data?.reviewer?.username || `Reviewer ${data.reviewer?.id || 'Unknown'}`;
  const email = data?.reviewer?.email || null;
  const displayRole = isApproved ? 'Reviewer' : 'Reviewer';

  const statusText =
    data.stage?.approvalType?.name || (isApproved ? 'Approved' : 'Rejected');

  const reasonText = isApproved ? null : data.rejectionReason;

  return (
    <div className={styles.reasonItem}>
      <div className={styles.reasonHeader}>
        <div className={styles.userInfo}>
          <Typography className={styles.username}>{username}</Typography>
          <Typography className={styles.role}>{displayRole}</Typography>
        </div>
        <span className={`${styles.statusLabel} ${statusClass}`}>
          {formattedDate}
        </span>
      </div>
      {email && (
        <div className={styles.emailContainer}>
          <Typography variant="bodySmall" className={styles.email}>
            {email}
          </Typography>
        </div>
      )}

      {reasonText && (
        <div
          className={`${styles.reasonContainer} ${
            isApproved ? styles.approved : styles.rejected
          }`}
        >
          <Typography
            variant="bodysmall"
            className={`${styles.reasonLabel} ${statusClass}`}
          >
            {isApproved ? 'Approval Reason:' : 'Rejection Reason:'}
          </Typography>
          <Typography
            variant="bodyDefault"
            className={`${styles.reasonText} ${statusClass}`}
          >
            {reasonText}
          </Typography>
        </div>
      )}
    </div>
  );
};

export default ApprovalRejectionReasonItem;
